import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiService } from '../../demands/services/api'
import { toast } from '../../../components/Toast'
import type { Processo } from '../../../models'

// Tipos para os payloads
type CreateProcessoPayload = {
  nome_processo: string
  prioridade_baixa: number
  prioridade_media: number
  prioridade_alta: number
  prioridade_urgente: number
  setor_processo_fk: number
  descricao: string
  tipo?: string
  instrucoes?: string
  links?: Array<{ link: string; titulo?: string }>
  autorizacoes?: Array<{ autorizador_fk: number; ordem: number | null }>
  gestor_demandante?: boolean
  gestor_demandado?: boolean
  executivo_demandante?: boolean
  executivo_demandado?: boolean
  equipe?: boolean
} | FormData

// Filtros para processos
export type ProcessosFilters = {
  buscar?: string
  setor?: number
}

// Query keys
export const processosKeys = {
  all: ['processos'] as const,
  lists: () => [...processosKeys.all, 'list'] as const,
  list: (filters?: ProcessosFilters) => [...processosKeys.lists(), filters] as const,
  details: () => [...processosKeys.all, 'detail'] as const,
  detail: (id: number) => [...processosKeys.details(), id] as const,
}

// Opções adicionais para o hook
type UseProcessosOptions = {
  enabled?: boolean
}

// Hook para listar processos
export const useProcessos = (filters?: ProcessosFilters, options?: UseProcessosOptions) => {
  return useQuery({
    queryKey: processosKeys.list(filters),
    queryFn: async () => {
      return await apiService.getProcessosList(filters)
    },
    staleTime: 1000 * 60 * 5, // 5 minutos
    refetchOnMount: true, // Sempre refetch ao montar
    enabled: options?.enabled ?? true,
  })
}

// Hook para criar processo
export const useCreateProcesso = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (data: CreateProcessoPayload) => {
      return await apiService.createProcesso(data)
    },
    onSuccess: () => {
      // Invalidar e refetch cache de processos para recarregar a lista
      // type: 'all' força refetch mesmo de queries inativas
      queryClient.invalidateQueries({ queryKey: processosKeys.lists() })
      queryClient.refetchQueries({ queryKey: processosKeys.lists(), type: 'all' })
      toast.success('Processo criado com sucesso!')
    },
    onError: (error) => {
      console.error('Erro ao criar processo:', error)
      toast.error('Erro ao criar processo. Tente novamente.')
    },
  })
}

// Hook para atualizar processo
export const useUpdateProcesso = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: CreateProcessoPayload }) => {
      return await apiService.updateProcesso(id, data)
    },
    onSuccess: (_, variables) => {
      // Invalidar e refetch cache de processos para recarregar a lista
      // type: 'all' força refetch mesmo de queries inativas
      queryClient.invalidateQueries({ queryKey: processosKeys.lists() })
      queryClient.refetchQueries({ queryKey: processosKeys.lists(), type: 'all' })
      // Invalidar cache do processo específico
      queryClient.invalidateQueries({ queryKey: processosKeys.detail(variables.id) })
      toast.success('Processo atualizado com sucesso!')
    },
    onError: (error) => {
      console.error('Erro ao atualizar processo:', error)
      toast.error('Erro ao atualizar processo. Tente novamente.')
    },
  })
}

// Hook para deletar processo
export const useDeleteProcesso = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (processo: Processo) => {
      return await apiService.deleteProcesso(processo.id)
    },
    onSuccess: () => {
      // Invalidar e refetch cache de processos para recarregar a lista
      // type: 'all' força refetch mesmo de queries inativas
      queryClient.invalidateQueries({ queryKey: processosKeys.lists() })
      queryClient.refetchQueries({ queryKey: processosKeys.lists(), type: 'all' })
      toast.success('Processo excluído com sucesso!')
    },
    onError: (error) => {
      console.error('Erro ao excluir processo:', error)
      toast.error('Erro ao excluir processo. Tente novamente.')
    },
  })
}
